import { lazy, Suspense, useState } from "react";
import { Brain, Hand, PartyPopper, Puzzle, Music2, Palette, ShoppingBasket } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { SectionHead, Reveal } from "@/components/Reveal";
import { MemoryGame, WhackGame } from "@/components/Games";
import { ORCHESTRA_T } from "@/components/games/OrchestraGame.labels";
import { COLOR_T } from "@/components/games/ColorGame.labels";
import { BASKET_T } from "@/components/games/BasketGame.labels";

// Heavier games are code-split: downloaded only when their tab is opened.
const PuzzleGame = lazy(() => import("@/components/PuzzleGame").then((m) => ({ default: m.PuzzleGame })));
const OrchestraGame = lazy(() => import("@/components/games/OrchestraGame"));
const ColorGame = lazy(() => import("@/components/games/ColorGame"));
const BasketGame = lazy(() => import("@/components/games/BasketGame"));
const GAMES = { puzzle: PuzzleGame, orchestra: OrchestraGame, color: ColorGame, basket: BasketGame };
import { ColoringGrid } from "@/components/ColoringGrid";

export const GamesSection = () => {
  const { t, lang } = useI18n();
  const { kits } = useSite();
  const [game, setGame] = useState("memory");
  const [theme, setTheme] = useState("Compleanno");
  const list = Array.isArray(kits) ? kits : [];
  const themes = [...new Set(list.map((k) => k.theme).filter(Boolean))];
  const shown = theme && themes.includes(theme) ? list.filter((k) => k.theme === theme) : list;
  const lab = (T) => (T[lang] ?? T.it).tab;
  const tabs = [["memory", t.games.memory, Brain], ["whack", t.games.whack, Hand], ["puzzle", t.games.puzzle.tab, Puzzle], ["orchestra", lab(ORCHESTRA_T), Music2], ["color", lab(COLOR_T), Palette], ["basket", lab(BASKET_T), ShoppingBasket]];
  const Lazy = GAMES[game];

  return (
    <section id="giochi" className="relative overflow-hidden bg-leaf/15 py-24 sm:py-32 dots-bg" data-testid="games-section">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.games.eyebrow} title={t.games.title} sub={t.games.sub} testid="games-section-head" eyebrowClass="bg-leaf !text-white" />
        <Reveal className="mt-12 rounded-[32px] sticker bg-cream p-5 sm:p-8">
          <div className="flex flex-wrap gap-2">
            {tabs.map(([k, label, Icon]) => (
              <button key={k} onClick={() => setGame(k)} className={`inline-flex items-center gap-2 rounded-full border-[2.5px] border-ink px-5 py-2 font-display font-semibold transition-colors ${game === k ? "bg-sun" : "bg-white hover:bg-sun/40"}`} data-testid={`games-tab-${k}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          <div className="mt-6">{game === "memory" ? <MemoryGame /> : game === "whack" ? <WhackGame /> : <Suspense fallback={<div className="min-h-[320px]" aria-busy="true" />}><Lazy /></Suspense>}</div>
        </Reveal>

        <div className="mt-20" data-testid="party-kit">
          <h3 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3"><PartyPopper className="w-8 h-8 text-pink" /> {t.games.kitTitle}</h3>
          <p className="mt-2 text-ink/75 font-semibold">{t.games.kitSub}</p>
          {themes.length > 1 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {["", ...themes].map((th) => (
                <button key={th || "all"} onClick={() => setTheme(th)} className={`rounded-full border-2 border-ink px-4 py-1.5 text-sm font-extrabold ${theme === th ? "bg-pink text-white" : "bg-white"}`} data-testid={`kit-theme-${th || "all"}`}>
                  {th ? (t.games.kitThemes?.[th] ?? th) : t.games.all}
                </button>
              ))}
            </div>
          )}
          <ColoringGrid items={shown} />
        </div>
      </div>
    </section>
  );
};
